/** Limits from IMPLEMENTATION_PLAN.md section 10. */
export const MAX_FILE_BYTES = 200 * 1024 * 1024;
export const MAX_SECONDS = 20 * 60;
export const MIN_SECONDS = 1;

export const ACCEPT =
  'audio/*,video/mp4,video/webm,.mp3,.wav,.m4a,.aac,.ogg,.oga,.opus,.flac,.mp4,.m4v,.webm';

const MIME_BY_EXTENSION: Record<string, string> = {
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  m4a: 'audio/mp4',
  aac: 'audio/aac',
  ogg: 'audio/ogg',
  oga: 'audio/ogg',
  opus: 'audio/opus',
  flac: 'audio/flac',
  mp4: 'video/mp4',
  m4v: 'video/mp4',
  webm: 'video/webm',
};

export function extensionOf(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  return dot >= 0 ? fileName.slice(dot + 1).toLowerCase() : '';
}

export function mimeTypeOf(file: { name: string; type: string }): string {
  if (file.type !== '') return file.type;
  return MIME_BY_EXTENSION[extensionOf(file.name)] ?? 'application/octet-stream';
}

export function titleFromFileName(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  const base = dot > 0 ? fileName.slice(0, dot) : fileName;
  return base.trim() || fileName;
}

/** A user-facing reason the file can't be used before reading it, or null. */
export function checkFileBeforeReading(file: {
  name: string;
  size: number;
  type: string;
}): string | null {
  if (file.size === 0) return 'This file is empty.';
  if (file.size > MAX_FILE_BYTES) {
    return `This file is ${Math.round(file.size / 1024 ** 2)} MB. Files up to 200 MB are supported.`;
  }
  const mime = mimeTypeOf(file);
  if (
    !mime.startsWith('audio/') &&
    !mime.startsWith('video/') &&
    !(extensionOf(file.name) in MIME_BY_EXTENSION)
  ) {
    return `"${file.name}" doesn't look like an audio or video file.`;
  }
  return null;
}

export function checkDuration(seconds: number): string | null {
  if (seconds < MIN_SECONDS) return 'This file is too short to analyze.';
  if (seconds > MAX_SECONDS) {
    return `This recording is ${Math.round(seconds / 60)} minutes long. Songs up to 20 minutes are supported.`;
  }
  return null;
}

/** Video containers: the app uses only their sound track (F1.2). */
const VIDEO_EXTENSIONS = new Set(['mp4', 'm4v', 'webm', 'mov', 'mkv', 'avi']);

export function decodeErrorMessage(fileName: string): string {
  const ext = extensionOf(fileName);
  if (VIDEO_EXTENSIONS.has(ext)) {
    return `This browser can't read the sound of this .${ext} video. MP4 (with AAC sound) and WebM videos work in most browsers.`;
  }
  return ext
    ? `This browser can't decode .${ext} files. Try MP3, WAV or M4A.`
    : "This browser can't decode this file. Try MP3, WAV or M4A.";
}
